import React from 'react'
import { AuthNotice } from './auth-card'

// A removed membership at SSO sign-in, in words (spec
// 2026-10-23-scim-provisioning-design R25, owner decision 2026-10-23: "it
// should show/state why"). SSO sign-in never brings back a membership an
// organization admin, EducationXR staff, the partner API or the
// organization's identity provider (SCIM) removed. Auth says which and how
// in these places, each read here into the same entries ({ name,
// byIdentityProvider }):
//
//   - GET /auth-provider/:token/details' removedOrganizations ([{
//     organizationID, organizationName, via, removedAt, byIdentityProvider,
//     reason, message }], omitted when empty): the organizations this
//     sign-in did not bring the person back into while another one admitted
//     them (fromRemovedOrganizations; /open/organization, /open/complete).
//   - The /auth/error link of a sign-in nothing else admitted:
//     reason=membership_removed&notice=<handle>, then organization=<name>
//     and via=<how> once per organization (most recent first, at most 5).
//     GET /auth-provider/removed-notice/:handle (no session; a read-only
//     handle, never the sign-in token) answers { removedOrganizations: [{
//     organizationName, via, removedAt }] } (fromRemovedOrganizations), or
//     404 removed_notice_unavailable. The link's own names (fromQuery) are
//     only the fallback: anyone can craft one, so what it can make the page
//     say is held small: each name is one line of at most NAME_MAX
//     characters, at most MAX_ENTRIES of them, and a via that isn't one of
//     VIAS is ignored (never shown).
//   - A refused pick's 403 { reason/code membership_removed, removed: [{
//     organizationName, via }] } (fromRemovedList).
//
// The sentences are the spec's: "Your access to <Organization> was removed
// by an administrator. Ask them to restore it." / "Your organization's
// identity provider turned off your access to <Organization>."

export const VIAS = ['admin', 'staff', 'partner_api', 'scim']
export const NAME_MAX = 100
export const MAX_ENTRIES = 10

// One line of plain text, at most NAME_MAX characters ('' for nothing).
export const cleanName = value => {
  // eslint-disable-next-line no-control-regex
  const text = String(value == null ? '' : value).replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g, ' ').replace(/\s+/g, ' ').trim()
  const chars = [...text]
  return chars.length > NAME_MAX ? `${chars.slice(0, NAME_MAX - 1).join('').trimEnd()}…` : text
}

export const cleanVia = value => (VIAS.includes(value) ? value : null)

const entry = (name, via, byIdentityProvider) => ({ name: cleanName(name), byIdentityProvider: Boolean(byIdentityProvider) || cleanVia(via) === 'scim' })

// A removedOrganizations list: GET /:token/details', or the removed notice's.
export const fromRemovedOrganizations = answer => {
  const list = answer && Array.isArray(answer.removedOrganizations) ? answer.removedOrganizations : []
  return list
    .filter(r => r && typeof r === 'object')
    .slice(0, MAX_ENTRIES)
    .map(r => entry(r.organizationName, r.via, r.byIdentityProvider === true))
}

export const fromDetails = fromRemovedOrganizations

// Whether a notice= looks like auth's handle (a prefix and 43 base64url
// characters today): anything else is never sent anywhere.
export const isNoticeHandle = value => typeof value === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(value)

// A refused pick's removed list ({ organizationName, via }).
export const fromRemovedList = list =>
  (Array.isArray(list) ? list : []).filter(r => r && typeof r === 'object').slice(0, MAX_ENTRIES).map(r => entry(r.organizationName, r.via))

// The /auth/error link's organization= and via= (URLSearchParams), paired
// in order; a via with no name, or a name that cleans to nothing, is
// dropped.
export const fromQuery = query => {
  if (!query || typeof query.getAll !== 'function') return []
  const names = query.getAll('organization')
  const vias = query.getAll('via')
  const out = []
  for (let i = 0; i < names.length && out.length < MAX_ENTRIES; i++) {
    const e = entry(names[i], vias[i])
    if (e.name) out.push(e)
  }
  // A link with a via and no name: who removed them, not where.
  if (out.length === 0 && vias.some(v => cleanVia(v))) out.push({ name: '', byIdentityProvider: vias.every(v => cleanVia(v) === 'scim') })
  return out
}

// "Acme", "Acme and Bolt", "Acme, Bolt and Cedar"; none named: fallback.
export const namesText = (names, fallback = 'your EducationXR organization') => {
  const list = (names || []).filter(Boolean)
  if (list.length === 0) return fallback
  if (list.length === 1) return list[0]
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}

// The sentences for entries: removals by a person first, then the identity
// provider's ([] for none).
export const removedSentences = entries => {
  const list = Array.isArray(entries) ? entries : []
  if (list.length === 0) return []
  const byPeople = list.filter(e => !e.byIdentityProvider)
  const byIdP = list.filter(e => e.byIdentityProvider)
  const out = []
  if (byPeople.length) out.push(`Your access to ${namesText(byPeople.map(e => e.name))} was removed by an administrator. Ask them to restore it.`)
  if (byIdP.length) out.push(`Your organization's identity provider turned off your access to ${namesText(byIdP.map(e => e.name))}.`)
  return out
}

// Whether every entry was the identity provider's.
export const allByIdentityProvider = entries => Array.isArray(entries) && entries.length > 0 && entries.every(e => e.byIdentityProvider)

// The one-line notice of a sign-in that went on into another organization
// (R25): nothing when there is nothing to say.
export const RemovedMembershipNotice = ({ entries }) => {
  const sentences = removedSentences(entries)
  if (sentences.length === 0) return null
  // In its own token scope: /open/complete's card isn't an AuthPage.
  return (
    <div className="sw-workspace">
      <AuthNotice tone="warn">
        <p data-testid="removed-membership-notice">{sentences.join(' ')}</p>
      </AuthNotice>
    </div>
  )
}
